import { Alert, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './login.module.css'
import { api } from '@/lib/api'

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmailPage,
})

function getToken(): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get('token')
}

function VerifyEmailPage() {
  const token = getToken()
  const [isLoading, setIsLoading] = useState(!!token)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(
    token ? null : 'Missing verification token. Please check your email link.'
  )

  useEffect(() => {
    if (!token) return

    api
      .verifyEmail({ token })
      .then(() => {
        setIsSuccess(true)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Email verification failed')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [token])

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🔄</span>
            <span className={styles.logoText}>BounceBack</span>
          </div>

          {isLoading && (
            <>
              <h2 className={styles.title}>Verifying your email</h2>
              <p className={styles.subtitle}>Please wait while we verify your email address...</p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <Spinner size="lg" />
              </div>
            </>
          )}

          {isSuccess && (
            <>
              <h2 className={styles.title}>Email verified</h2>
              <p className={styles.subtitle}>
                Your email has been successfully verified. You can now sign in to your account.
              </p>
              <Alert variant="success" className={styles.alert}>
                Your email address has been confirmed.
              </Alert>
              <div className={styles.actions}>
                <Link to="/login" className={styles.fullWidth}>
                  <button
                    type="button"
                    className={styles.fullWidth}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Go to Login
                  </button>
                </Link>
              </div>
            </>
          )}

          {error && (
            <>
              <h2 className={styles.title}>Verification failed</h2>
              <p className={styles.subtitle}>We were unable to verify your email address.</p>
              <Alert variant="danger" className={styles.alert}>
                {error}
              </Alert>
              <p className={styles.footer}>
                Need help? <Link to="/login">Back to login</Link>
              </p>
            </>
          )}
        </CardBody>
      </Card>
    </main>
  )
}
